import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { initials, avatarUrl } from '../lib/format.js';
import Icon from './Icon.jsx';
import Illustration from './Illustration.jsx';

/* ============================================================================
   Toasts
   ========================================================================= */

const ToastContext = createContext(null);

const TOAST_ICON = { success: 'check-circle', error: 'warning', info: 'info' };

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => setToasts((t) => t.filter((x) => x.id !== id)), []);

  const push = useCallback(
    (message, tone = 'info', ttl = 4200) => {
      const id = Math.random().toString(36).slice(2);
      setToasts((t) => [...t, { id, message, tone }]);
      if (ttl) setTimeout(() => dismiss(id), ttl);
      return id;
    },
    [dismiss]
  );

  const value = useMemo(
    () => ({
      push,
      success: (m) => push(m, 'success'),
      error: (m) => push(typeof m === 'string' ? m : m?.message || 'Something went wrong', 'error', 6000),
      dismiss,
    }),
    [push, dismiss]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-host" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.tone}`}>
            <span className="toast-icon">
              <Icon name={TOAST_ICON[t.tone] || 'info'} />
            </span>
            <div className="grow">{t.message}</div>
            <button onClick={() => dismiss(t.id)} aria-label="Dismiss">
              <Icon name="close" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);

/* ============================================================================
   Identity — avatars, ratings, status
   ========================================================================= */

export function Avatar({ name, avatar, size = '', className = '', ring = false }) {
  const src = avatarUrl(avatar);
  return (
    <div
      className={`avatar ${size ? `avatar-${size}` : ''} ${ring ? 'avatar-ring' : ''} ${className}`}
      aria-hidden="true"
    >
      {src ? <img src={src} alt="" className="avatar-img" loading="lazy" decoding="async" /> : initials(name) || '?'}
    </div>
  );
}

/** Read-only rating. Renders filled/empty stars plus the numeric value. */
export function Stars({ value = 0, count, size = 14 }) {
  const full = Math.round(Number(value) || 0);
  return (
    <span className="rating-line">
      <span className="stars" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((n) => (
          <Icon
            key={n}
            name={n <= full ? 'star-filled' : 'star'}
            size={size}
            style={n <= full ? undefined : { opacity: 0.4 }}
          />
        ))}
      </span>
      <b>{Number(value || 0).toFixed(1)}</b>
      {count !== undefined && <span>({count})</span>}
      <span className="sr-only">{`${Number(value || 0).toFixed(1)} out of 5`}</span>
    </span>
  );
}

export function StarInput({ value, onChange }) {
  return (
    <div className="star-input" role="radiogroup" aria-label="Rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={`${n} star${n > 1 ? 's' : ''}`}
          className={n <= value ? 'on' : ''}
          onClick={() => onChange(n)}
        >
          <Icon name={n <= value ? 'star-filled' : 'star'} size={34} strokeWidth={1.5} />
        </button>
      ))}
    </div>
  );
}

const STATUS_TONE = {
  pending: 'pill-warning',
  accepted: 'pill-success',
  completed: 'pill-info',
  declined: 'pill-danger',
  cancelled: 'pill-danger',
  verified: 'pill-success',
  rejected: 'pill-danger',
  paid: 'pill-success',
  unpaid: 'pill',
  open: 'pill-warning',
  resolved: 'pill-success',
  dismissed: 'pill',
};

const STATUS_ICON = {
  pending: 'hourglass',
  accepted: 'check-circle',
  completed: 'flag',
  declined: 'x-circle',
  cancelled: 'ban',
  verified: 'shield-check',
  rejected: 'x-circle',
  paid: 'check',
  open: 'megaphone',
  resolved: 'check-circle',
  dismissed: 'ban',
};

export function StatusPill({ status, label, icon = true }) {
  const glyph = STATUS_ICON[status];
  return (
    <span className={`pill ${STATUS_TONE[status] || ''}`}>
      {icon && glyph && <Icon name={glyph} />}
      {label || status}
    </span>
  );
}

/* ============================================================================
   States — empty, loading, error
   ========================================================================= */

/**
 * Empty/zero state. `art` names an illustration; `icon` remains supported for
 * call sites that pass an emoji. An action element renders below the copy.
 */
export function Empty({ art, icon, title, children, action, actions }) {
  return (
    <div className="empty">
      {art ? (
        <div className="empty-art">
          <Illustration name={art} />
        </div>
      ) : icon ? (
        <div className="empty-icon" aria-hidden="true">
          {icon}
        </div>
      ) : null}
      {title && <h3>{title}</h3>}
      {children && <p>{children}</p>}
      {(action || actions) && <div className="empty-actions">{action || actions}</div>}
    </div>
  );
}

export function Spinner({ label, size }) {
  return (
    <span className="row-tight" role="status">
      <span className={`spinner ${size === 'lg' ? 'spinner-lg' : ''}`} />
      {label && <span className="small muted">{label}</span>}
    </span>
  );
}

/** Single shimmer block. Width/height accept any CSS length. */
export function Skeleton({ w = '100%', h = 12, r, circle = false, className = '', style }) {
  return (
    <div
      className={`skeleton ${circle ? 'skeleton-circle' : 'skeleton-text'} ${className}`}
      style={{ width: w, height: h, borderRadius: r, ...style }}
      aria-hidden="true"
    />
  );
}

/** Skeleton list of cards — the default loading state for a page of rows. */
export function Loading({ rows = 3, variant = 'row' }) {
  if (variant === 'stat') {
    return (
      <div className="grid grid-4" aria-busy="true" aria-label="Loading">
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="stat">
            <Skeleton w="55%" h={9} />
            <Skeleton w="70%" h={26} style={{ marginTop: 10 }} />
            <Skeleton w="45%" h={9} style={{ marginTop: 8 }} />
          </div>
        ))}
      </div>
    );
  }

  if (variant === 'card') {
    return (
      <div className="nurse-grid" aria-busy="true" aria-label="Loading">
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="nurse-card">
            <div className="row-tight" style={{ alignItems: 'flex-start' }}>
              <Skeleton w={46} h={46} circle />
              <div className="grow">
                <Skeleton w="60%" h={13} />
                <Skeleton w="85%" h={10} style={{ marginTop: 8 }} />
              </div>
              <Skeleton w={54} h={22} r={8} />
            </div>
            <Skeleton w="70%" h={10} />
            <div className="row-tight">
              <Skeleton w={78} h={26} r={999} />
              <Skeleton w={64} h={26} r={999} />
            </div>
            <div className="row-tight" style={{ marginTop: 4 }}>
              <Skeleton h={34} r={999} />
              <Skeleton h={34} r={999} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="stack" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="card card-pad">
          <div className="row-tight" style={{ alignItems: 'flex-start' }}>
            <Skeleton w={42} h={42} r={12} />
            <div className="grow">
              <Skeleton w="42%" h={13} />
              <Skeleton w="72%" h={10} style={{ marginTop: 9 }} />
              <Skeleton w="54%" h={10} style={{ marginTop: 7 }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ============================================================================
   Overlays — modal / bottom sheet
   ========================================================================= */

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Dialog that renders as a centred card on desktop and a bottom sheet on
 * phones. Handles Escape, background-scroll locking, initial focus, focus
 * return, and a Tab trap so keyboard users cannot wander behind the overlay.
 */
export function Modal({ title, onClose, children, footer, wide = false, size, bodyClass = '' }) {
  const ref = useRef(null);
  const restoreTo = useRef(null);

  useEffect(() => {
    restoreTo.current = document.activeElement;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Focus the dialog itself so the first Tab lands on its first control.
    ref.current?.focus();

    const onKey = (e) => {
      if (e.key === 'Escape') return onClose?.();
      if (e.key !== 'Tab' || !ref.current) return;
      const items = [...ref.current.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      restoreTo.current?.focus?.();
    };
  }, [onClose]);

  const maxWidth = size ?? (wide ? 720 : undefined);

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div
        ref={ref}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        style={maxWidth ? { maxWidth } : undefined}
      >
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
        </div>
        <div className={`modal-body ${bodyClass}`}>{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

/** Bottom sheet — the same overlay, used where a sheet reads better than a dialog. */
export const Sheet = Modal;

/** Confirmation dialog for destructive or irreversible actions. */
export function ConfirmDialog({ title, message, confirmLabel = 'Confirm', tone = 'danger', busy, onConfirm, onClose, children }) {
  return (
    <Modal
      title={title}
      onClose={onClose}
      size={440}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className={`btn btn-${tone}`} onClick={onConfirm} disabled={busy}>
            {busy ? <span className="spinner" /> : confirmLabel}
          </button>
        </>
      }
    >
      {message && <p className="muted">{message}</p>}
      {children}
    </Modal>
  );
}

/* ============================================================================
   Data display
   ========================================================================= */

export function Stat({ label, value, sub, tone, icon }) {
  return (
    <div className="stat">
      <div className="stat-head">
        <span className="stat-label">{label}</span>
        {icon && (
          <span className={`stat-icon ${tone ? `icon-tile-${tone}` : ''}`}>
            <Icon name={icon} />
          </span>
        )}
      </div>
      <div className="stat-value" style={tone ? { color: `var(--${tone})` } : undefined}>
        {value}
      </div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

export function BarChart({ data, valueKey = 'value', labelKey = 'label', format = (v) => v }) {
  const max = Math.max(...data.map((d) => Number(d[valueKey]) || 0), 1);
  if (!data.length) return <p className="small dim center">No data yet.</p>;
  return (
    <div className="bars">
      {data.map((d, i) => (
        <div className="bar-col" key={i}>
          <div className="bar-value">{format(d[valueKey])}</div>
          <div
            className="bar"
            style={{ height: `${Math.max((Number(d[valueKey]) / max) * 100, 2)}%` }}
            title={`${d[labelKey]}: ${format(d[valueKey])}`}
          />
          <div className="bar-label">{d[labelKey]}</div>
        </div>
      ))}
    </div>
  );
}

/** Accessible tab strip. `tabs` is [{ key, label, badge }]; renders the bar only. */
export function Tabs({ tabs, active, onChange }) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={active === t.key}
          className="tab"
          onClick={() => onChange(t.key)}
        >
          {t.label}
          {t.badge ? <span className="tab-badge">{t.badge}</span> : null}
        </button>
      ))}
    </div>
  );
}

/** Pill-style segmented control — for mutually exclusive list filters. */
export function Segmented({ options, value, onChange, counts, className = '', style }) {
  return (
    <div className={`segmented ${className}`} style={style} role="group">
      {options.map(([v, label]) => (
        <button key={v || 'all'} type="button" aria-pressed={value === v} onClick={() => onChange(v)}>
          {label}
          {counts?.[v] ? <span className="seg-count">{counts[v]}</span> : null}
        </button>
      ))}
    </div>
  );
}

/** Determinate progress bar for multi-step flows. */
export function Progress({ value, max = 100, label }) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div
      className="progress"
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <i style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Label/value row used in summaries and detail panels. */
export function KeyValue({ label, children, action }) {
  return (
    <div className="summary-row">
      <dt>{label}</dt>
      <dd>
        {children}
        {action}
      </dd>
    </div>
  );
}

/* ============================================================================
   Form helpers
   ========================================================================= */

/** Labelled form control with hint and inline error. */
export function Field({ label, hint, error, children, id, required, optional, className = '' }) {
  return (
    <div className={`field ${className}`}>
      {label && (
        <label htmlFor={id}>
          {label}
          {required && (
            <span className="field-required" aria-hidden="true">
              *
            </span>
          )}
          {optional && <span className="dim" style={{ fontWeight: 400 }}> · optional</span>}
        </label>
      )}
      {children}
      {error ? (
        <div className="field-error">
          <Icon name="warning" />
          {error}
        </div>
      ) : hint ? (
        <div className="hint">{hint}</div>
      ) : null}
    </div>
  );
}

/** Search input with a leading magnifier and a clear affordance. */
export function SearchInput({ value, onChange, placeholder = 'Search…', label = 'Search', className = '', ...rest }) {
  const id = useId();
  return (
    <div className={`input-icon search-bar ${className}`}>
      <Icon name="search" />
      <input
        id={id}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={label}
        {...rest}
      />
      {value && (
        <button type="button" className="input-clear" onClick={() => onChange('')} aria-label="Clear search">
          <Icon name="close" />
        </button>
      )}
    </div>
  );
}

/** Accessible on/off switch with a label to its right. */
export function Switch({ checked, onChange, label, hint, id }) {
  const auto = useId();
  const inputId = id || auto;
  return (
    <div className="switch-row">
      <span className="switch">
        <input id={inputId} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span className="knob" />
      </span>
      <label htmlFor={inputId} className="grow" style={{ cursor: 'pointer' }}>
        <span className="t-title" style={{ display: 'block' }}>
          {label}
        </span>
        {hint && <span className="tiny dim">{hint}</span>}
      </label>
    </div>
  );
}

/* ============================================================================
   Page furniture
   ========================================================================= */

/** Standard page title block: optional avatar/icon, title, subtitle, actions. */
export function PageHeader({ title, subtitle, avatar, icon, children, badge }) {
  return (
    <div className="page-head">
      {avatar}
      {icon && !avatar && (
        <span className="icon-tile icon-tile-lg">
          <Icon name={icon} />
        </span>
      )}
      <div className="grow">
        <div className="row-tight" style={{ flexWrap: 'wrap' }}>
          <h1>{title}</h1>
          {badge}
        </div>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

/** Section heading with an optional trailing link/action. */
export function SectionHeader({ title, subtitle, children, as: Tag = 'h2' }) {
  return (
    <div className="section-head">
      <div className="grow">
        <Tag>{title}</Tag>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

/** "Live"/"cached" indicator used by polled screens. */
export function LiveBadge({ stale }) {
  return (
    <span className="live-chip">
      {stale ? (
        <>
          <Icon name="wifi-off" /> cached
        </>
      ) : (
        <>
          <span className="live-dot" aria-hidden="true" /> live
        </>
      )}
    </span>
  );
}

export { Icon, Illustration };
